import useSpacesList from 'hooks/useSpacesList';
import { TFunction } from 'i18next';
import { Space } from 'interfaces/space';
import { useRouter } from 'next/router';
import { Controller, useForm } from 'react-hook-form';
import { Post as PostType } from 'types/post';

import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@thinkific/chalk/Button';
import { Select } from '@thinkific/chalk/Select';
import { Text } from '@thinkific/chalk/Text';
import { XCircle } from '@thinkific/chalk/react-icons';

import { useCommunityContext } from 'components/CommunityContext';
import PostBody from 'components/Post/components/PostBody';
import { RichTextEditorHandle } from 'components/RichTextEditor/RichTextEditor';
import UserAvatar from 'components/UserAvatar';

import * as css from './PostForm.css';

interface PostFormProps {
  cancelEdit?: () => void;
  onSubmit: (
    data: FormValues,
    { reset, editorRef }: { reset: () => void; editorRef: React.RefObject<RichTextEditorHandle> }
  ) => Promise<void>;
  isSubmitting: boolean;
  spacesList: { value: string; label: string; dangerousFontFamily?: string }[];
  selectedSpace: Space;
  post: PostType;
  postReactionCount: number;
  totalCommentCount: number;
  t: TFunction;
}

export interface FormValues {
  spaceId: string;
}

const MovePostForm = ({
  onSubmit,
  isSubmitting,
  cancelEdit,
  spacesList,
  selectedSpace,
  postReactionCount,
  totalCommentCount,
  t,
  post,
}: PostFormProps) => {
  const { currentUser } = useCommunityContext();
  const editorRef = useRef<RichTextEditorHandle>(null);
  const { firstPostableSpace } = useSpacesList();
  const [selectedSpaceId, setSelectedSpaceId] = useState(
    selectedSpace?.id || firstPostableSpace?.value || ''
  );

  // Get user avatar info
  const { id: userId, avatarUrl, fullName } = currentUser;

  const { control, handleSubmit, getValues, setValue, reset } = useForm({
    defaultValues: { spaceId: selectedSpaceId },
  });

  const router = useRouter();

  useEffect(() => {
    // If the spaceId is not set, set it to the first postable space
    const spaceId = getValues('spaceId');
    if (isSubmitting) return;
    if (!spaceId || (router.query.spaceId && selectedSpaceId !== router.query.spaceId)) {
      const fallbackSpaceId = selectedSpace?.id || firstPostableSpace?.value || '';
      setValue('spaceId', fallbackSpaceId);
      setSelectedSpaceId(fallbackSpaceId);
    }
  }, [spacesList, selectedSpace, router.query.spaceId, isSubmitting]);

  const handleFormSubmit = async (data) => {
    await onSubmit(data, { reset, editorRef });
  };

  return (
    <div className={css.formWrapper} data-qa="post-form">
      <UserAvatar
        avatarUrl={avatarUrl}
        size="sm"
        userId={userId}
        username={fullName || ''}
        shouldOpenProfile
      />
      <div className={css.formLabel}>
        <div className={css.spaceSelectContainer}>
          <Text variant="body.md">{t('components-postForm-move_post_in', 'Move post to')}</Text>
          <div className={css.spaceSelectionActions}>
            <Controller
              control={control}
              name="spaceId"
              render={({ field }) => (
                <div data-user-pilot-id="post-form-space-select">
                  <Select
                    a11yLabel={t('components-forms-post-postForm-select_a_space', 'Select a space')}
                    className={css.spaceSelect}
                    dataQa="post-form__space-select"
                    defaultValue={firstPostableSpace?.value}
                    label={t('components-forms-post-postForm-select_a_space', 'Select a space')}
                    options={spacesList}
                    placeholder={t(
                      'components-forms-post-postForm-select_a_space',
                      'Select a space'
                    )}
                    value={field.value}
                    hiddenLabel
                    required
                    onChange={(value) => {
                      setSelectedSpaceId(value);
                      field.onChange(value);
                    }}
                  />
                </div>
              )}
            />
            <div className={css.submitRow}>
              <Button
                dataQa="post-form__submit"
                isLoading={isSubmitting}
                size="xs"
                type="submit"
                variant="primary"
                onClick={handleSubmit(handleFormSubmit)}
              >
                {t('components-richTextEditor-save', 'Save')}
              </Button>
              <Button
                emphasis="low"
                icon={XCircle}
                size="sm"
                type="button"
                variant="default"
                hiddenLabel
                onClick={cancelEdit}
              >
                {t('Cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className={css.editorWrapper}>
        <PostBody
          className={css.postBody}
          footerClassName={css.postFooter}
          post={post}
          postReactionCount={postReactionCount}
          t={t}
          totalCommentCount={totalCommentCount}
        />
      </div>
    </div>
  );
};

export default MovePostForm;
